import {
  Flex,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  MenuItem,
  Box,
} from "@chakra-ui/react"
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  InfoOutlineIcon,
} from "@chakra-ui/icons"
import { memo, useEffect, useRef } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  AVATAR_LAYER_ORDER,
  AvatarOptionsData,
  selectCharacterAvatar,
  updateCharacterAvatar,
} from "./characterSlice"
import type React from "react"
import {
  EditedAvatarProvider,
  useEditedAvatar,
  useEditedAvatarActions,
} from "./useEditedCharacter"
import { useAvatarEditorModal } from "./useAvatarEditorModal"
import type { WarmedImage } from "../game/turnTransition"
import { onIdle, warmImage, withCap } from "../game/turnTransition"

type AvatarLayerName = (typeof AVATAR_LAYER_ORDER)[number]

/** Upper bound on waiting for a layer that is still decoding when stepped to. */
const LAYER_WAIT_MS = 200

export const MenuCharacterAvatarEditor: React.FC = () => (
  <EditedAvatarProvider data={useAppSelector(selectCharacterAvatar)}>
    <MenuCharacterEditorWithContext />
  </EditedAvatarProvider>
)

const MenuCharacterEditorWithContext: React.FC = () => {
  const dispatch = useAppDispatch()
  const { openModal, closeModal, isOpen } = useAvatarEditorModal()
  const savedAvatar = useAppSelector(selectCharacterAvatar)

  // Actions only: the modal shell must not re-render on every arrow tap
  const { setEditedAvatar, getEditedAvatar } = useEditedAvatarActions()

  // The provider mounts once with the menu and outlives modal open/close, so
  // re-seed the draft from the committed avatar on every open. Closing without
  // saving therefore discards edits.
  useEffect(() => {
    if (isOpen) setEditedAvatar(savedAvatar)
  }, [isOpen, savedAvatar, setEditedAvatar])

  const handleSaveClick = () => {
    dispatch(updateCharacterAvatar(getEditedAvatar()))
    closeModal()
  }

  return (
    <MenuItem onClick={openModal}>
      <Flex alignItems="center">
        <InfoOutlineIcon aria-label="Edit character avatar" mr={2} />
        <Text>Edit Character</Text>
        <Modal size="full" isOpen={isOpen} onClose={closeModal}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Change Your Avatar</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {/* Clamp the figure so gutter + figure always fit the modal
                  body: on narrow phones 75vh alone overflows the viewport. */}
              <CharacterAvatarEditor height="min(75vh, calc((100vw - 150px) * 2))" />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleSaveClick}>
                Save
              </Button>
              <Button variant="ghost" onClick={closeModal}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    </MenuItem>
  )
}

// Every layer is centred on the same origin so the artwork stays registered
// and never clips against the container at smaller heights.
const makeLayerStyle = (height: string): React.CSSProperties => ({
  height,
  objectFit: "cover",
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1,
})

// Width of the label column to the left of the figure. The control rows span
// gutter + figure, so the pills sit beside the artwork, never on it.
const LABEL_GUTTER = "clamp(88px, 16vw, 150px)"

/** Human-readable names for each avatar layer, shown left of its row. */
const LAYER_LABELS: Record<string, string> = {
  skinLayer: "Skin Tone",
  hairLayer: "Hair",
  eyebrowsLayer: "Eyebrows",
  glassesLayer: "Glasses",
  faceLayer: "Expression",
  chestLayer: "Outfit",
  shoesLayer: "Shoes",
}

const labelForLayer = (name: string) =>
  LAYER_LABELS[name] ??
  name
    .replace(/Layer$/, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, character => character.toUpperCase())
    .trim()

export const CharacterAvatarEditor: React.FC<{ height?: string }> = ({
  height = "75vh",
}) => {
  // The draft context is the single source of truth for the editor preview;
  // it is seeded from the committed avatar by the hosting provider.
  const { editedAvatar, setEditedAvatar } = useEditedAvatar()
  const layerStyle = makeLayerStyle(height)

  // Every option layer is fetched and decoded shortly after the editor opens,
  // so stepping through options swaps already-decoded pictures. The warmed
  // elements are kept for the editor's lifetime to keep the bitmaps alive.
  const warmed = useRef(new Map<string, WarmedImage>())
  useEffect(() => {
    const idle = onIdle(() => {
      for (const options of Object.values(AvatarOptionsData)) {
        for (const src of options) {
          if (!warmed.current.has(src)) {
            warmed.current.set(src, warmImage({ image: src }))
          }
        }
      }
    }, 100)
    return () => idle.cancel()
  }, [])

  const stepLayer = async (name: AvatarLayerName, delta: number) => {
    const options = AvatarOptionsData[name]
    if (options.length === 0) return
    const currentIndex = Math.max(0, options.indexOf(editedAvatar[name]))
    const nextIndex = (currentIndex + delta + options.length) % options.length
    const newSrc = options[nextIndex]
    if (!newSrc) return
    // Swap only once the new layer is decoded (bounded), so the old layer is
    // never left on screen under the new label.
    const image = warmed.current.get(newSrc) ?? warmImage({ image: newSrc })
    warmed.current.set(newSrc, image)
    await withCap(image.ready, LAYER_WAIT_MS)
    setEditedAvatar(prev => ({ ...prev, [name]: newSrc }))
  }

  // Catalog-driven: iterate the canonical layer list, not persisted keys, so a
  // stale avatar object from an old session can't add or reorder layers.
  const clothingLayers = AVATAR_LAYER_ORDER.filter(
    key => key !== "skinLayer" && editedAvatar[key],
  )
  const controlRows = AVATAR_LAYER_ORDER.filter(
    key => AvatarOptionsData[key].length > 1,
  )

  return (
    <Box width="100%">
      <Flex alignItems="center" justifyContent="center">
        {/* The wrapper owns the label gutter and anchors the control rows, so
            rows can extend beside the figure box despite its overflow:hidden */}
        <Box position="relative" pl={LABEL_GUTTER}>
          <Box
            aspectRatio={0.5}
            height={height}
            position="relative"
            overflow="hidden"
            boxShadow="md"
          >
            {/* 1. Skin — opaque figure fill, bottom of the stack */}
            {editedAvatar.skinLayer && (
              <img
                src={editedAvatar.skinLayer}
                alt=""
                decoding="async"
                style={layerStyle}
              />
            )}
            {/* 2. Eyes — always above skin */}
            <img
              src="/character/eyes-neutral.png"
              alt=""
              decoding="async"
              style={layerStyle}
            />
            {/* 3. Outline — line-art overlay above the skin fill and eyes,
                below clothing. Hardcoded: never user-changeable. */}
            <img
              src="/character/outline.png"
              alt=""
              decoding="async"
              style={layerStyle}
            />
            {/* 4. Remaining avatar layers (clothing & features above outline) */}
            {clothingLayers.map(key => (
              <img
                key={key}
                src={editedAvatar[key]}
                alt=""
                decoding="async"
                style={layerStyle}
              />
            ))}
          </Box>
          {controlRows.map((key, index) => (
            <LayerControlRow
              key={key}
              name={key}
              index={index}
              rowCount={controlRows.length}
              value={editedAvatar[key]}
              onStep={stepLayer}
            />
          ))}
        </Box>
      </Flex>
    </Box>
  )
}

// Memoised on its own value: an arrow tap re-renders the stepped row and the
// preview, not all seven rows.
const LayerControlRow = memo(function LayerControlRow({
  name,
  index,
  rowCount,
  onStep,
}: {
  name: AvatarLayerName
  index: number
  rowCount: number
  /** The row's current option (only used to decide when to re-render) */
  value: string
  onStep: (name: AvatarLayerName, delta: number) => void
}) {
  const step = (delta: number) => onStep(name, delta)

  const label = labelForLayer(name)
  // Rows are spread evenly down the figure so they scale with any height
  const rowTop = `${((index + 0.5) * 100) / rowCount}%`

  return (
    <Flex
      position="absolute"
      top={rowTop}
      left={0}
      right={0}
      transform="translateY(-50%)"
      alignItems="center"
      gap={1}
      zIndex={2}
      pointerEvents="none"
    >
      <Flex
        width={LABEL_GUTTER}
        flexShrink={0}
        justifyContent="flex-end"
        pr={2}
      >
        <Text
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="wide"
          color="gray.700"
          bg="whiteAlpha.900"
          borderWidth="1px"
          borderColor="blackAlpha.200"
          borderRadius="full"
          px={2}
          py={0.5}
          textAlign="center"
        >
          {label}
        </Text>
      </Flex>
      <IconButton
        icon={<ArrowLeftIcon />}
        aria-label={`Previous ${label}`}
        size="sm"
        pointerEvents="auto"
        onClick={() => step(-1)}
      />
      {/* Transparent spacer spanning the figure between the arrows */}
      <Box flex={1} />
      <IconButton
        icon={<ArrowRightIcon />}
        aria-label={`Next ${label}`}
        size="sm"
        pointerEvents="auto"
        onClick={() => step(1)}
      />
    </Flex>
  )
})
